import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Container,
    alpha,
    Modal,
    IconButton,
    CardMedia
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PeopleIcon from '@mui/icons-material/People';
import BookOnlineIcon from '@mui/icons-material/BookOnline';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

const Home = () => {
    const navigate = useNavigate();
    const [selectedImage, setSelectedImage] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

     const logoColors = {
        primaryBlue: '#3f51b5', // Main blue from logo
        brightYellow: '#c4d835', // Bright yellow/lime from logo
        darkBlue: '#2c387e', // Darker blue for depth
        lightYellow: '#d4e157', // Lighter yellow for highlights
    };

    // Replace these placeholder images with your actual images
    const galleryImages = [
        {
            id: 1,
            src: '/main-court.jpeg', // Replace with your image paths
            alt: 'Futsal Court View 1',
            title: 'Main Court'
        },
        {
            id: 2,
            src: '/turf.jpeg',
            alt: 'Futsal Court View 2',
            title: 'Night Game'
        },
        {
            id: 3,
            src: '/practice.jpeg',
            alt: 'Futsal Court View 3',
            title: 'The Grid Scnz'
        },
        {
            id: 4,
            src: '/DSC04684.jpg',
            alt: 'Futsal Court View 4',
            title: 'Team Action'
        },
        {
            id: 5,
            src: '/academy.jpeg',
            alt: 'Futsal Court View 5',
            title: 'Academy Setup'
        },
        
    ];

    const features = [
        {
            icon: <SportsSoccerIcon sx={{ fontSize: 40, color: logoColors.brightYellow }} />,
            title: "Professional Court",
            description: "State-of-the-art futsal court with premium artificial turf and professional lighting"
        },
        {
            icon: <AccessTimeIcon sx={{ fontSize: 40, color: logoColors.brightYellow }} />,
            title: "Flexible Timing",
            description: "Available 24/7 with flexible time slots to fit your schedule perfectly"
        },
        {
            icon: <BookOnlineIcon sx={{ fontSize: 40, color: logoColors.brightYellow }} />,
            title: "Easy Booking",
            description: "Simple online booking system with instant confirmation and secure payment"
        }
    ];

    const handleImageClick = (image, index) => {
        setSelectedImage(image);
        setCurrentImageIndex(index);
    };

    const handleCloseModal = () => {
        setSelectedImage(null);
    };

    const handlePrevImage = () => {
        const newIndex = currentImageIndex > 0 ? currentImageIndex - 1 : galleryImages.length - 1;
        setCurrentImageIndex(newIndex);
        setSelectedImage(galleryImages[newIndex]);
    };

    const handleNextImage = () => {
        const newIndex = currentImageIndex < galleryImages.length - 1 ? currentImageIndex + 1 : 0;
        setCurrentImageIndex(newIndex);
        setSelectedImage(galleryImages[newIndex]);
    };

    return (
        <Box sx={{ minHeight: '100vh' }}>
            {/* Hero Section */}
            <Box
                sx={{
                    position: 'relative',
                    minHeight: '85vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    background: 'transparent',
                    py: 8
                }}
            >
                <Container maxWidth="lg">
                    {/* Glass-morphism Hero Card */}
                    <Box
                        sx={{
                            background: `linear-gradient(135deg, ${logoColors.primaryBlue}f0 0%, ${logoColors.darkBlue}f0 100%)`,
                            backdropFilter: 'blur(20px)',
                            borderRadius: '24px',
                            border: `2px solid ${logoColors.brightYellow}40`,
                            p: { xs: 4, md: 8 },
                            boxShadow: `0 20px 60px ${logoColors.primaryBlue}30`,
                            textAlign: 'center',
                            color: 'white',
                            position: 'relative',
                            overflow: 'hidden',
                        }}
                    >
                        <Typography 
                            variant="h1" 
                            component="h1" 
                            gutterBottom 
                            sx={{
                                fontWeight: 800,
                                fontSize: { xs: '3rem', md: '5rem' },
                                background: `linear-gradient(45deg, ${logoColors.brightYellow} 30%, ${logoColors.lightYellow} 90%)`,
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                letterSpacing: '-0.02em',
                                mb: 2
                            }}
                        >
                            THE GRID
                        </Typography>

                        <Typography 
                            variant="h1" 
                            component="h1" 
                            gutterBottom 
                            sx={{
                                fontWeight: 800,
                                fontSize: { xs: '1.5rem', md: '2.5rem' },
                                background: `linear-gradient(45deg, ${logoColors.brightYellow} 30%, ${logoColors.lightYellow} 90%)`,
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                letterSpacing: '-0.02em',
                                mb: 2
                            }}
                        >
                            BY FC ROVERS
                        </Typography>
                        
                        <Typography 
                            variant="h4" 
                            sx={{
                                color: logoColors.brightYellow,
                                fontWeight: 600,
                                fontSize: { xs: '1.5rem', md: '2.5rem' },
                                mb: 3,
                                textShadow: `0 2px 4px ${logoColors.darkBlue}80`
                            }}
                        >
                            Picture Perfect, Culture Driven
                        </Typography>
                        
                        <Typography 
                            variant="h6" 
                            sx={{
                                color: logoColors.brightYellow,
                                maxWidth: 800,
                                mx: 'auto',
                                mb: 6,
                                fontSize: { xs: '1.1rem', md: '1.3rem' },
                                lineHeight: 1.8,
                                fontWeight: 400,
                                textShadow: `0 2px 4px ${logoColors.darkBlue}80`
                            }}
                        >
                            Experience the ultimate futsal destination in Karachi. Our premium facility offers 
                            professional-grade courts, modern amenities, and an unmatched playing experience 
                            for players of all skill levels.
                        </Typography>

                         <Typography 
                            variant="h4" 
                            sx={{
                                color: logoColors.lightYellow,
                                fontWeight: 600,
                                fontSize: { xs: '1.1rem', md: '1.3rem' },
                                mb: 3,
                                 textShadow: `0 2px 4px ${logoColors.darkBlue}80`
                            }}
                        >
                            For queries, Please whatsapp us at +92 323 8148153
                        </Typography>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                            <Button 
                                variant="contained" 
                                size="large" 
                                onClick={() => navigate('/booking')}
                                sx={{ 
                                    px: 6, 
                                    py: 2,
                                    fontSize: '1.2rem',
                                    fontWeight: 700,
                                    borderRadius: '12px',
                                    background: `linear-gradient(45deg, ${logoColors.brightYellow} 30%, ${logoColors.lightYellow} 90%)`,
                                    color: logoColors.darkBlue,
                                    boxShadow: `0 8px 30px ${logoColors.brightYellow}40`,
                                    textTransform: 'none',
                                    '&:hover': {
                                        transform: 'translateY(-3px)',
                                        boxShadow: `0 12px 40px ${logoColors.brightYellow}50`,
                                        background: `linear-gradient(45deg, ${logoColors.lightYellow} 30%, ${logoColors.brightYellow} 90%)`,
                                    },
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                }}
                            >
                                Book Your Court
                            </Button>
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* Gallery Section */}
            <Box sx={{ py: 10 }}>
                <Container maxWidth="lg">
                    <Box
                        sx={{
                            background: `linear-gradient(135deg, ${logoColors.primaryBlue}f0 0%, ${logoColors.darkBlue}f0 100%)`,
                            borderRadius: '24px',
                            p: { xs: 4, md: 6 },
                            textAlign: 'center',
                            color: logoColors.lightYellow,
                            backdropFilter: 'blur(10px)',
                            boxShadow: `0 20px 60px ${logoColors.primaryBlue}30`,
                            border: `2px solid ${logoColors.brightYellow}30`,
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: `linear-gradient(45deg, ${logoColors.brightYellow}10 0%, transparent 100%)`,
                                zIndex: 1
                            }
                        }}
                    >
                        <Box sx={{ position: 'relative', zIndex: 2 }}>
                            <Typography 
                                variant="h3" 
                                component="h2" 
                                gutterBottom
                                sx={{ 
                                    fontWeight: 800,
                                    mb: 3,
                                    fontSize: { xs: '2.5rem', md: '3.5rem',
                                    background: `linear-gradient(45deg, ${logoColors.brightYellow} 30%, ${logoColors.lightYellow} 90%)`,
                                    backgroundClip: 'text',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent', }
                                }}
                            >
                                Ready to Play?
                            </Typography>
                            <Typography 
                                variant="h6" 
                                sx={{ 
                                    mb: 5,
                                    opacity: 0.95,
                                    lineHeight: 1.7,
                                    fontSize: { xs: '1.1rem', md: '1.3rem' },
                                    maxWidth: 600,
                                    mx: 'auto',
                                    color: logoColors.lightYellow
                                }}
                            >
                                Join the hundreds of players who choose The Grid for their futsal experience.Take a look at our state-of-the-art facility and see why we're Karachi's premier futsal destination.
                            </Typography>
                            
                            {/* Gallery Grid */}
                            <Grid 
                                container 
                                spacing={3} 
                                sx={{ 
                                    mt: 2,
                                    justifyContent: 'center'
                                }}
                            >
                                {galleryImages.map((image, index) => (
                                    <Grid item xs={12} sm={6} md={4} key={image.id} sx={{ 
                                            display: 'flex'
                                        }}>
                                        <Card
                                            onClick={() => handleImageClick(image, index)}
                                            sx={{
                                                cursor: 'pointer',
                                                borderRadius: '16px',
                                                overflow: 'hidden',
                                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                background: `${logoColors.primaryBlue}20`,
                                                backdropFilter: 'blur(10px)',
                                                border: `2px solid ${logoColors.brightYellow}40`,
                                                width: '100%',
                                                height: '300px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                '&:hover': {
                                                    transform: 'translateY(-8px) scale(1.02)',
                                                    boxShadow: `0 20px 40px ${logoColors.primaryBlue}40`,
                                                    border: `2px solid ${logoColors.brightYellow}80`,
                                                }
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    height: '240px',
                                                    overflow: 'hidden',
                                                    position: 'relative'
                                                }}
                                            >
                                            <CardMedia
                                                component="img"
                                                image={image.src}
                                                alt={image.alt}
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    objectPosition: 'center',
                                                    transition: 'transform 0.3s ease',
                                                    '&:hover': {
                                                        transform: 'scale(1.1)'
                                                    }
                                                }}
                                                onError={(e) => {
                                                    // Fallback for missing images
                                                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjY2NjIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIFBsYWNlaG9sZGVyPC90ZXh0Pjwvc3ZnPg==';
                                                }}
                                            />
                                            </Box>
                                            <CardContent sx={{ 
                                                height: '60px',
                                                p: 2, 
                                                background: `linear-gradient(135deg, ${logoColors.darkBlue}90 0%, ${logoColors.primaryBlue}90 100%)`,
                                                flexGrow: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                <Typography 
                                                    variant="subtitle1" 
                                                    sx={{ 
                                                        color: logoColors.brightYellow,
                                                        fontWeight: 700,
                                                        textAlign: 'center',
                                                        fontSize: '1rem'
                                                    }}
                                                >
                                                    {image.title}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                            
                            {/* Social Media Section */}
                            <Box sx={{ 
                                mt: 6, 
                                display: 'flex', 
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 3 
                            }}>
                                <Typography 
                                    variant="h5" 
                                    sx={{ 
                                        fontWeight: 700,
                                        color: logoColors.brightYellow,
                                        textAlign: 'center',
                                        fontSize: { xs: '1.5rem', md: '1.8rem' }
                                    }}
                                >
                                    Follow Us
                                </Typography>
                                <Box sx={{ 
                                    display: 'flex', 
                                    gap: 4,
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    alignItems: 'center'
                                }}>
                                    {/* Instagram */}
                                    <Box
                                        component="a"
                                        href="https://instagram.com/thegridbyrovers"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            padding: '12px 24px',
                                            background: `linear-gradient(45deg, ${logoColors.primaryBlue} 0%, ${logoColors.darkBlue} 100%)`,
                                            borderRadius: '30px',
                                            textDecoration: 'none',
                                            color: logoColors.brightYellow,
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            border: `2px solid ${logoColors.brightYellow}40`,
                                            '&:hover': {
                                                transform: 'translateY(-3px)',
                                                boxShadow: `0 10px 25px ${logoColors.primaryBlue}40`,
                                                textDecoration: 'none',
                                                color: logoColors.brightYellow,
                                                background: `linear-gradient(45deg, ${logoColors.darkBlue} 0%, ${logoColors.primaryBlue} 100%)`,
                                                border: `2px solid ${logoColors.brightYellow}80`,
                                            }
                                        }}
                                    >
                                        <Box sx={{ 
                                            width: 36, 
                                            height: 36, 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center' ,
                                            background: 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)',
                                            borderRadius: '50%',
                                            padding: '8px'
                                        }}>
                                            <svg 
                                                width="32" 
                                                height="32" 
                                                viewBox="0 0 24 24" 
                                                fill="white"
                                            >
                                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                                            </svg>
                                        </Box>
                                        <Box>
                                            <Typography 
                                                variant="body1" 
                                                sx={{ fontWeight: 600, fontSize: '1.1rem' }}
                                            >
                                                @thegridbyrovers
                                            </Typography>
                                            <Typography 
                                                variant="caption" 
                                                sx={{ opacity: 0.9, fontSize: '0.85rem' }}
                                            >
                                                Follow us on Instagram
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Facebook */}
                                    <Box
                                        component="a"
                                        href="https://www.facebook.com/share/1Gd8Ujvwtp/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            padding: '12px 24px',
                                            background: `linear-gradient(45deg, ${logoColors.primaryBlue} 0%, ${logoColors.darkBlue} 100%)`,
                                            borderRadius: '30px',
                                            textDecoration: 'none',
                                            color: logoColors.brightYellow,
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            border: `2px solid ${logoColors.brightYellow}40`,
                                            '&:hover': {
                                                transform: 'translateY(-3px)',
                                                boxShadow: `0 10px 25px ${logoColors.primaryBlue}40`,
                                                textDecoration: 'none',
                                                color: logoColors.brightYellow,
                                                background: `linear-gradient(45deg, ${logoColors.darkBlue} 0%, ${logoColors.primaryBlue} 100%)`,
                                                border: `2px solid ${logoColors.brightYellow}80`,
                                            }
                                        }}
                                    >
                                        <Box sx={{ 
                                            width: 36, 
                                            height: 36, 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center' ,
                                            background: '#1877f2',
                                            borderRadius: '50%',
                                            padding: '8px'
                                        }}>
                                            <svg 
                                                width="32" 
                                                height="32" 
                                                viewBox="0 0 24 24" 
                                                fill="white"
                                            >
                                                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                            </svg>
                                        </Box>
                                        <Box>
                                            <Typography 
                                                variant="body1" 
                                                sx={{ fontWeight: 600, fontSize: '1.1rem' }}
                                            >
                                                The Grid by Rovers
                                            </Typography>
                                            <Typography 
                                                variant="caption" 
                                                sx={{ opacity: 0.9, fontSize: '0.85rem' }}
                                            >
                                                Follow us on Facebook
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* Image Modal */}
            <Modal
                open={!!selectedImage}
                onClose={handleCloseModal}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        maxWidth: '90vw',
                        maxHeight: '90vh',
                        outline: 'none',
                        borderRadius: '16px',
                        overflow: 'hidden',
                        boxShadow: `0 25px 50px ${logoColors.primaryBlue}50`,
                        border: `2px solid ${logoColors.brightYellow}60`
                    }}
                >
                    {selectedImage && (
                        <>
                            <img
                                src={selectedImage.src}
                                alt={selectedImage.alt}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                    display: 'block'
                                }}
                                onError={(e) => {
                                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAwIiBoZWlnaHQ9IjYwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjVmNWY1Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIyNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIE5vdCBGb3VuZDwvdGV4dD48L3N2Zz4=';
                                }}
                            />
                            
                            {/* Close Button */}
                            <IconButton
                                onClick={handleCloseModal}
                                sx={{
                                    position: 'absolute',
                                    top: 16,
                                    right: 16,
                                    background: `${logoColors.primaryBlue}cc`,
                                    color: logoColors.brightYellow,
                                    '&:hover': {
                                        background: `${logoColors.darkBlue}cc`,
                                        color: logoColors.lightYellow
                                    }
                                }}
                            >
                                <CloseIcon />
                            </IconButton>

                            {/* Navigation Buttons */}
                            <IconButton
                                onClick={handlePrevImage}
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: 16,
                                    transform: 'translateY(-50%)',
                                    background: `${logoColors.primaryBlue}cc`,
                                    color: logoColors.brightYellow,
                                    '&:hover': {
                                        background: `${logoColors.darkBlue}cc`,
                                        color: logoColors.lightYellow
                                    }
                                }}
                            >
                                <ArrowBackIosIcon />
                            </IconButton>

                            <IconButton
                                onClick={handleNextImage}
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    right: 16,
                                    transform: 'translateY(-50%)',
                                    background: `${logoColors.primaryBlue}cc`,
                                    color: logoColors.brightYellow,
                                    '&:hover': {
                                        background: `${logoColors.darkBlue}cc`,
                                        color: logoColors.lightYellow
                                    }
                                }}
                            >
                                <ArrowForwardIosIcon />
                            </IconButton>

                            {/* Image Title */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    background: `linear-gradient(transparent, ${logoColors.primaryBlue}cc)`,
                                    color: logoColors.brightYellow,
                                    p: 3,
                                    textAlign: 'center'
                                }}
                            >
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {selectedImage.title}
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                                    {currentImageIndex + 1} of {galleryImages.length}
                                </Typography>
                            </Box>
                        </>
                    )}
                </Box>
            </Modal>
        </Box>
    );
};

export default Home;